import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, MessageCircle, Search } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface PatternRow {
  senderId: string;
  volume: number;
  sampleBody: string;
  remark: string | null;
  updatedAt: string | null;
}

const FAMILIES: Array<[string, RegExp]> = [
  ['HDFC', /HDFC/i],
  ['IndusInd', /INDUS/i],
  ['Axis', /AXIS|AxisBk/i],
  ['ICICI', /ICICI/i],
  ['HSBC', /HSBC/i],
  ['Amex', /AMEX|MYAMEX|AMERIC/i],
  ['Canara', /CANBNK|CANARA/i],
  ['Kotak', /KOTAK/i],
  ['SBI', /\bSBI\b/i],
  ['RBL', /\bRBL\b/i],
  ['Citi', /CITI/i],
  ['Equitas', /EQUTAS|EQTSBK|ESFB/i],
  ['Federal', /FEDR/i],
  ['Yes', /YESBNK/i],
  ['IDFC', /IDFC/i],
  ['BOB', /BOBSMS|^BOB/i],
  ['PNB', /\bPNB\b/i],
  ['MMT/myPartner', /MMTRIP|MAKEMY|MMT/i],
  ['TBO', /eTIKET|TBO/i],
  ['Riya', /RIAOTP|RIYA/i],
  ['Indigo', /INDIGO/i],
  ['Vistara', /VISTAR/i],
];

function familyFor(senderId: string): string {
  for (const [name, rx] of FAMILIES) if (rx.test(senderId)) return name;
  return 'Other';
}

export function SmsPatternsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [family, setFamily] = useState<string>('');

  const list = useQuery<PatternRow[]>({
    queryKey: ['sms-patterns'],
    queryFn: () => api('/api/sms-patterns'),
    refetchOnWindowFocus: false,
  });

  const groupedFamilies = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of list.data ?? []) {
      const f = familyFor(r.senderId);
      counts.set(f, (counts.get(f) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [list.data]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (list.data ?? []).filter((r) => {
      if (family && familyFor(r.senderId) !== family) return false;
      if (!q) return true;
      return (
        r.senderId.toLowerCase().includes(q) ||
        r.sampleBody.toLowerCase().includes(q) ||
        (r.remark ?? '').toLowerCase().includes(q)
      );
    });
  }, [list.data, filter, family]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<MessageCircle className="h-5 w-5" />}
        title="SMS sender patterns"
        subtitle="Every distinct sender + one representative OTP body. Write a remark per row — Claude reads these to refine routing & template mining."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by sender / body / remark"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-8"
          />
        </div>
        <button
          type="button"
          onClick={() => setFamily('')}
          className={cn(
            'rounded-full border px-3 py-1 text-xs transition',
            !family ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background hover:bg-muted',
          )}
        >
          All ({list.data?.length ?? 0})
        </button>
        {groupedFamilies.map(([name, n]) => (
          <button
            key={name}
            type="button"
            onClick={() => setFamily(family === name ? '' : name)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs transition',
              family === name
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background hover:bg-muted',
            )}
          >
            {name} ({n})
          </button>
        ))}
      </div>

      {list.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading senders…
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((row) => (
            <PatternRowCard
              key={row.senderId}
              row={row}
              onSaved={() => qc.invalidateQueries({ queryKey: ['sms-patterns'] })}
            />
          ))}
          {visible.length === 0 && (
            <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No senders match.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PatternRowCard({ row, onSaved }: { row: PatternRow; onSaved: () => void }) {
  const [draft, setDraft] = useState(row.remark ?? '');
  const dirty = draft !== (row.remark ?? '');

  const save = useMutation({
    mutationFn: () =>
      api(`/api/sms-patterns/${encodeURIComponent(row.senderId)}/remark`, {
        method: 'POST',
        body: { remark: draft },
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <code className="rounded bg-muted px-2 py-0.5 text-xs font-mono">{row.senderId}</code>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {familyFor(row.senderId)}
          </span>
          <span className="text-[10px] text-muted-foreground">· {row.volume.toLocaleString()} SMSes</span>
          {row.updatedAt && (
            <span className="text-[10px] text-emerald-600">
              · remarked {new Date(row.updatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 break-words rounded bg-muted/40 p-2 font-mono text-xs leading-relaxed">
        {row.sampleBody}
      </div>
      <div className="mt-2 flex gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a remark — what this sender is, how to route it, what's special. Claude reads this."
          rows={2}
          className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
        />
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="self-end"
        >
          {save.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : save.isSuccess && !dirty ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
}
